import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, Strikethrough, List, ListOrdered, Undo, Redo } from 'lucide-react';

interface RichTextEditorProps {
  initialContent?: string;
  content?: string;
  onChange?: (html: string) => void;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent = '',
  content,
  onChange,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
    ],
    content: content ?? initialContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) onChange(html);
    },
  });

  // Sync external content prop into editor when it changes
  useEffect(() => {
    if (editor && content !== undefined && editor.getHTML() !== content) {
      editor.commands.setContent(content, false);
    }
  }, [editor, content]);

  const handleButtonClick = (action: () => void) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    action();
    editor?.commands.focus();
  };

  return (
    <div className="rich-text-editor">
      <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-t-lg bg-gray-50">
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().toggleBold().run())}
          disabled={!editor?.can().chain().focus().toggleBold().run()}
          className={`p-2 rounded-lg hover:bg-gray-200 ${
            editor?.isActive('bold') ? 'bg-primary-100 text-blue-600' : 'text-gray-600'
          }`}
        >
          <Bold className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().toggleItalic().run())}
          disabled={!editor?.can().chain().focus().toggleItalic().run()}
          className={`p-2 rounded-lg hover:bg-gray-200 ${
            editor?.isActive('italic') ? 'bg-primary-100 text-blue-600' : 'text-gray-600'
          }`}
        >
          <Italic className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().toggleStrike().run())}
          disabled={!editor?.can().chain().focus().toggleStrike().run()}
          className={`p-2 rounded-lg hover:bg-gray-200 ${
            editor?.isActive('strike') ? 'bg-primary-100 text-blue-600' : 'text-gray-600'
          }`}
        >
          <Strikethrough className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().toggleBulletList().run())}
          className={`p-2 rounded-lg hover:bg-gray-200 ${
            editor?.isActive('bulletList') ? 'bg-primary-100 text-blue-600' : 'text-gray-600'
          }`}
        >
          <List className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().toggleOrderedList().run())}
          className={`p-2 rounded-lg hover:bg-gray-200 ${
            editor?.isActive('orderedList') ? 'bg-primary-100 text-blue-600' : 'text-gray-600'
          }`}
        >
          <ListOrdered className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().undo().run())}
          disabled={!editor?.can().chain().focus().undo().run()}
          className="p-2 text-gray-600 rounded-lg hover:bg-gray-200"
        >
          <Undo className="w-5 h-5" />
        </button>
        <button
          type="button"
          onMouseDown={handleButtonClick(() => editor?.chain().focus().redo().run())}
          disabled={!editor?.can().chain().focus().redo().run()}
          className="p-2 text-gray-600 rounded-lg hover:bg-gray-200"
        >
          <Redo className="w-5 h-5" />
        </button>
      </div>
      <EditorContent editor={editor} className="tiptap-editor p-4 border border-t-0 rounded-b-lg" />
    </div>
  );
};